import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DailyIframe from '@daily-co/daily-js';
import { io } from 'socket.io-client';
import { getLiveClass } from '../utils/api';
import { FiSend, FiUsers, FiArrowLeft, FiWifi, FiWifiOff } from 'react-icons/fi';

export default function LiveClassPage() {
  const { classId }  = useParams();
  const { user }     = useAuth();
  const navigate     = useNavigate();

  // ── Refs ──
  const videoContainerRef = useRef(null);
  const callFrameRef      = useRef(null);
  const socketRef         = useRef(null);
  const chatEndRef        = useRef(null);
  const inputRef          = useRef(null);

  // ── State ──
  const [liveClass,    setLiveClass]    = useState(null);
  const [messages,     setMessages]     = useState([]);
  const [newMsg,       setNewMsg]       = useState('');
  const [participants, setParticipants] = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [connected,    setConnected]    = useState(false);
  const [announcement, setAnnouncement] = useState('');

  // ── Fetch class details ──
  useEffect(() => {
    getLiveClass(classId)
      .then(r => {
        setLiveClass(r.data.liveClass);
        setLoading(false);
      })
      .catch(() => {
        setError('Class not found or you are not enrolled.');
        setLoading(false);
      });
  }, [classId]);

  // ── Daily.co video setup ──
 useEffect(() => {
  if (!liveClass?.meetLink || !videoContainerRef.current) return;

  // Destroy any existing Daily instance before creating a new one
  if (callFrameRef.current) {
    callFrameRef.current.destroy();
    callFrameRef.current = null;
  }

  // Also destroy any globally existing Daily frames
  const existingFrames = DailyIframe.getCallInstance();
  if (existingFrames) {
    existingFrames.destroy();
  }

  const frame = DailyIframe.createFrame(videoContainerRef.current, {
    showLeaveButton:      false,
    showFullscreenButton: true,
    userName:             user?.fullName || 'Student',
    iframeStyle: {
      position:     'absolute',
      top:          0,
      left:         0,
      width:        '100%',
      height:       '100%',
      border:       'none',
    },
  });

  callFrameRef.current = frame;
  frame.join({ url: liveClass.meetLink });

  frame.on('participant-joined', () =>
    setParticipants(Object.keys(frame.participants()).length));
  frame.on('participant-left', () =>
    setParticipants(Object.keys(frame.participants()).length));
  frame.on('joined-meeting', () =>
    setParticipants(Object.keys(frame.participants()).length));

  return () => {
    frame.destroy();
    callFrameRef.current = null;
  };
}, [liveClass, user]);

  // ── Socket.io chat setup — uses YOUR server's event names ──
  useEffect(() => {
    if (!liveClass) return;

    const socket = io(process.env.REACT_APP_API_URL || 'http://localhost:5000', {
      auth: { token: localStorage.getItem('korpnex_token') },
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      // YOUR server uses 'join-room' with roomName, userName, role
      socket.emit('join-room', {
        roomName: classId,
        userName: user?.fullName || 'Student',
        role:     user?.role     || 'student',
      });
    });

    socket.on('disconnect', () => setConnected(false));

    // YOUR server sends 'chat-history' with last 50 messages
    socket.on('chat-history', (msgs) => {
      setMessages(msgs.map(m => ({
        id:       m.id,
        sender:   m.userName,
        senderId: m.senderId || '',
        text:     m.message,
        role:     m.role,
        time:     new Date(m.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
      })));
    });

    // YOUR server sends 'new-message' for each chat message
    socket.on('new-message', (msg) => {
      setMessages(prev => [...prev, {
        id:       msg.id,
        sender:   msg.userName,
        senderId: msg.senderId || '',
        text:     msg.message,
        role:     msg.role,
        time:     new Date(msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
      }]);
    });

    // YOUR server sends 'participant-count'
    socket.on('participant-count', (count) => setParticipants(count));

    // YOUR server sends 'user-joined'
    socket.on('user-joined', ({ userName, role }) => {
      setMessages(prev => [...prev, {
        id:       Date.now(),
        sender:   'System',
        senderId: 'system',
        text:     `${userName} joined the class`,
        role:     'system',
        time:     new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
      }]);
    });

    // YOUR server sends 'user-left'
    socket.on('user-left', ({ userName }) => {
      setMessages(prev => [...prev, {
        id:       Date.now(),
        sender:   'System',
        senderId: 'system',
        text:     `${userName} left the class`,
        role:     'system',
        time:     new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
      }]);
    });

    // YOUR server sends 'announcement'
    socket.on('announcement', ({ message }) => {
      setAnnouncement(message);
      setTimeout(() => setAnnouncement(''), 8000);
    });

    return () => { socket.disconnect(); };
  }, [liveClass, classId, user]);

  // ── Auto scroll chat to bottom ──
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send message — uses YOUR server's 'send-message' event ──
  const sendMessage = useCallback(() => {
    if (!newMsg.trim() || !socketRef.current) return;
    socketRef.current.emit('send-message', {
      roomName: classId,
      message:  newMsg.trim(),
      userName: user?.fullName || 'Student',
      role:     user?.role     || 'student',
    });
    setNewMsg('');
    inputRef.current?.focus();
  }, [newMsg, classId, user]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Loading / error states ──
  if (loading) return (
    <div style={styles.centerScreen}>
      <div style={styles.spinner}/>
      <p style={{ color: '#94a3b8', marginTop: 16, fontSize: 14 }}>Loading class...</p>
    </div>
  );

  if (error) return (
    <div style={styles.centerScreen}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
      <p style={{ color: '#f87171', fontSize: 15, marginBottom: 20 }}>{error}</p>
      <button onClick={() => navigate('/dashboard?tab=live')} style={styles.backBtn}>
        ← Back to Dashboard
      </button>
    </div>
  );

  const isEnded    = liveClass?.status === 'ended';
  const isScheduled = liveClass?.status === 'scheduled';
  const replayReady = liveClass?.replayUrl &&
    liveClass?.replayAvailableAt &&
    new Date() >= new Date(liveClass.replayAvailableAt);

  return (
    <div style={styles.root}>

      {/* ── Top bar ── */}
      <div style={styles.topBar}>
        <button onClick={() => navigate('/dashboard?tab=live')} style={styles.backButton}>
          <FiArrowLeft size={15}/> <span>Back</span>
        </button>

        <div style={styles.classInfo}>
          <span style={styles.classTitle}>{liveClass?.title}</span>
          <span style={styles.classMeta}>
            {liveClass?.subject}
            {liveClass?.teacher?.fullName && ` · 👨‍🏫 ${liveClass.teacher.fullName}`}
          </span>
        </div>

        <div style={styles.topBarRight}>
          {liveClass?.status === 'live' && (
            <span style={styles.liveBadge}>
              <span style={styles.liveDot}/> LIVE
            </span>
          )}
          {isEnded && <span style={styles.endedBadge}>✅ Ended</span>}
          {isScheduled && <span style={styles.scheduledBadge}>📅 Upcoming</span>}

          <span style={styles.participantCount}>
            <FiUsers size={13}/> {participants}
          </span>

          <span style={{ ...styles.connectionDot, color: connected ? '#22c55e' : '#ef4444' }}>
            {connected ? <FiWifi size={13}/> : <FiWifiOff size={13}/>}
          </span>
        </div>
      </div>

      {/* ── Announcement banner ── */}
      {announcement && (
        <div style={styles.announcement}>
          📢 <strong>Teacher:</strong> {announcement}
        </div>
      )}

      {/* ── Main layout: Video | Chat ── */}
      <div style={styles.mainArea}>

        {/* LEFT — Video / Whiteboard (Daily.co fills this) */}
        <div style={styles.videoArea}>
          <div ref={videoContainerRef} style={styles.videoContainer}/>

          {/* Overlay when class not live */}
          {!liveClass?.meetLink && (
            <div style={styles.noClassOverlay}>
              {isScheduled && (
                <>
                  <div style={{ fontSize: 56, marginBottom: 16 }}>⏰</div>
                  <h3 style={{ color: 'white', marginBottom: 8 }}>Class hasn't started yet</h3>
                  <p style={{ color: '#64748b', fontSize: 14 }}>
                    Scheduled for {new Date(liveClass?.scheduledAt).toLocaleDateString('en-IN', {
                      weekday: 'long', day: 'numeric', month: 'long'
                    })} at {new Date(liveClass?.scheduledAt).toLocaleTimeString('en-IN', {
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                </>
              )}
              {isEnded && !replayReady && (
                <>
                  <div style={{ fontSize: 56, marginBottom: 16 }}>🎬</div>
                  <h3 style={{ color: 'white', marginBottom: 8 }}>Class has ended</h3>
                  <p style={{ color: '#64748b', fontSize: 14 }}>Replay will be available soon.</p>
                </>
              )}
              {isEnded && replayReady && (
                <>
                  <div style={{ fontSize: 56, marginBottom: 16 }}>▶️</div>
                  <h3 style={{ color: 'white', marginBottom: 8 }}>Watch the replay</h3>
                  <a href={liveClass.replayUrl} target="_blank" rel="noreferrer"
                    style={styles.replayBtn}>
                    Watch Recording
                  </a>
                </>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — Chat panel */}
        <div style={styles.chatPanel}>

          {/* Chat header */}
          <div style={styles.chatHeader}>
            <span style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>💬 Live Chat</span>
            <span style={{ color: '#475569', fontSize: 12 }}>{messages.filter(m => m.role !== 'system').length} messages</span>
          </div>

          {/* Messages */}
          <div style={styles.messageList}>
            {messages.length === 0 && (
              <div style={styles.emptyChat}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>👋</div>
                <p>No messages yet.<br/>Say hi to the class!</p>
              </div>
            )}

            {messages.map((msg, i) => {
              const isMe     = msg.sender === user?.fullName;
              const isSystem = msg.role   === 'system';
              const isTeacher = msg.role  === 'teacher';

              if (isSystem) return (
                <div key={msg.id || i} style={styles.systemMsg}>{msg.text}</div>
              );

              return (
                <div key={msg.id || i} style={{
                  ...styles.msgWrapper,
                  alignItems: isMe ? 'flex-end' : 'flex-start',
                }}>
                  {!isMe && (
                    <span style={{
                      ...styles.msgSender,
                      color: isTeacher ? '#f59e0b' : '#94a3b8',
                    }}>
                      {isTeacher && '👨‍🏫 '}{msg.sender}
                    </span>
                  )}
                  <div style={{
                    ...styles.msgBubble,
                    background:    isMe ? '#2563eb' : isTeacher ? '#1e3a5f' : '#1e293b',
                    borderRadius:  isMe ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
                    border:        isTeacher ? '1px solid #2563eb44' : 'none',
                  }}>
                    {msg.text}
                  </div>
                  <span style={styles.msgTime}>{msg.time}</span>
                </div>
              );
            })}
            <div ref={chatEndRef}/>
          </div>

          {/* Input */}
          <div style={styles.inputArea}>
            <input
              ref={inputRef}
              value={newMsg}
              onChange={e => setNewMsg(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={connected ? 'Type a message...' : 'Connecting...'}
              disabled={!connected}
              style={{
                ...styles.chatInput,
                opacity: connected ? 1 : 0.5,
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!connected || !newMsg.trim()}
              style={{
                ...styles.sendBtn,
                opacity: (connected && newMsg.trim()) ? 1 : 0.4,
              }}
            >
              <FiSend size={14}/>
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Styles ──
const styles = {
  root: {
    display:        'flex',
    flexDirection:  'column',
    height:         '100vh',
    background:     '#0f172a',
    overflow:       'hidden',
    fontFamily:     'var(--font-display, system-ui, sans-serif)',
  },
  centerScreen: {
    display:         'flex',
    flexDirection:   'column',
    alignItems:      'center',
    justifyContent:  'center',
    height:          '100vh',
    background:      '#0f172a',
  },
  spinner: {
    width:           36,
    height:          36,
    border:          '3px solid #1e293b',
    borderTop:       '3px solid #2563eb',
    borderRadius:    '50%',
    animation:       'spin 0.8s linear infinite',
  },
  topBar: {
    height:          52,
    background:      '#1e293b',
    borderBottom:    '1px solid #334155',
    display:         'flex',
    alignItems:      'center',
    padding:         '0 16px',
    gap:             16,
    flexShrink:      0,
  },
  backButton: {
    background:      'none',
    border:          '1px solid #334155',
    borderRadius:    8,
    color:           '#94a3b8',
    cursor:          'pointer',
    display:         'flex',
    alignItems:      'center',
    gap:             6,
    fontSize:        13,
    padding:         '5px 10px',
    flexShrink:      0,
  },
  classInfo: {
    flex:            1,
    display:         'flex',
    flexDirection:   'column',
    gap:             2,
    overflow:        'hidden',
  },
  classTitle: {
    color:           'white',
    fontWeight:      700,
    fontSize:        15,
    whiteSpace:      'nowrap',
    overflow:        'hidden',
    textOverflow:    'ellipsis',
  },
  classMeta: {
    color:           '#64748b',
    fontSize:        12,
    whiteSpace:      'nowrap',
    overflow:        'hidden',
    textOverflow:    'ellipsis',
  },
  topBarRight: {
    display:         'flex',
    alignItems:      'center',
    gap:             12,
    flexShrink:      0,
  },
  liveBadge: {
    background:      '#dc2626',
    color:           'white',
    fontSize:        11,
    fontWeight:      700,
    padding:         '3px 10px',
    borderRadius:    999,
    display:         'flex',
    alignItems:      'center',
    gap:             5,
    letterSpacing:   '0.5px',
  },
  liveDot: {
    width:           6,
    height:          6,
    borderRadius:    '50%',
    background:      'white',
    display:         'inline-block',
    animation:       'pulse 1.2s ease-in-out infinite',
  },
  endedBadge: {
    color:           '#64748b',
    fontSize:        12,
    fontWeight:      600,
  },
  scheduledBadge: {
    color:           '#60a5fa',
    fontSize:        12,
    fontWeight:      600,
  },
  participantCount: {
    color:           '#64748b',
    fontSize:        13,
    display:         'flex',
    alignItems:      'center',
    gap:             4,
  },
  connectionDot: {
    display:         'flex',
    alignItems:      'center',
  },
  announcement: {
    background:      '#1e3a1e',
    border:          '1px solid #16a34a',
    color:           '#86efac',
    padding:         '10px 20px',
    fontSize:        13,
    flexShrink:      0,
    animation:       'fadeIn 0.3s ease',
  },
  mainArea: {
    flex:            1,
    display:         'flex',
    overflow:        'hidden',
  },
  videoArea: {
    flex:            1,
    position:        'relative',
    background:      '#020617',
  },
  videoContainer: {
    position:        'absolute',
    inset:           0,
    width:           '100%',
    height:          '100%',
  },
  noClassOverlay: {
    position:        'absolute',
    inset:           0,
    display:         'flex',
    flexDirection:   'column',
    alignItems:      'center',
    justifyContent:  'center',
    textAlign:       'center',
    padding:         40,
  },
  replayBtn: {
    background:      '#2563eb',
    color:           'white',
    padding:         '10px 24px',
    borderRadius:    10,
    textDecoration:  'none',
    fontWeight:      600,
    fontSize:        14,
    marginTop:       8,
  },
  chatPanel: {
    width:           300,
    background:      '#0f172a',
    borderLeft:      '1px solid #1e293b',
    display:         'flex',
    flexDirection:   'column',
    flexShrink:      0,
  },
  chatHeader: {
    padding:         '14px 16px',
    borderBottom:    '1px solid #1e293b',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'space-between',
    flexShrink:      0,
  },
  messageList: {
    flex:            1,
    overflowY:       'auto',
    padding:         '12px 14px',
    display:         'flex',
    flexDirection:   'column',
    gap:             10,
    scrollbarWidth:  'thin',
    scrollbarColor:  '#1e293b transparent',
  },
  emptyChat: {
    color:           '#334155',
    fontSize:        13,
    textAlign:       'center',
    marginTop:       40,
    lineHeight:      1.7,
  },
  systemMsg: {
    textAlign:       'center',
    color:           '#334155',
    fontSize:        11,
    padding:         '4px 0',
    fontStyle:       'italic',
  },
  msgWrapper: {
    display:         'flex',
    flexDirection:   'column',
    gap:             3,
  },
  msgSender: {
    fontSize:        11,
    fontWeight:      600,
  },
  msgBubble: {
    color:           'white',
    padding:         '8px 12px',
    fontSize:        13,
    maxWidth:        '88%',
    wordBreak:       'break-word',
    lineHeight:      1.5,
  },
  msgTime: {
    fontSize:        10,
    color:           '#334155',
  },
  inputArea: {
    padding:         '12px 14px',
    borderTop:       '1px solid #1e293b',
    display:         'flex',
    gap:             8,
    flexShrink:      0,
  },
  chatInput: {
    flex:            1,
    background:      '#1e293b',
    border:          '1px solid #334155',
    borderRadius:    8,
    padding:         '8px 12px',
    color:           'white',
    fontSize:        13,
    outline:         'none',
    fontFamily:      'inherit',
  },
  sendBtn: {
    background:      '#2563eb',
    border:          'none',
    borderRadius:    8,
    padding:         '8px 12px',
    color:           'white',
    cursor:          'pointer',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    flexShrink:      0,
  },
  backBtn: {
    background:      '#1e293b',
    border:          '1px solid #334155',
    borderRadius:    8,
    color:           '#94a3b8',
    cursor:          'pointer',
    padding:         '8px 16px',
    fontSize:        13,
  },
};